import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgentDto, PrinterDto, PrintJobDto } from '@thebb/shared';
import { del, get, patch, post } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, PageTitle, Spinner, fmtTime } from '../components/ui';
import { AutoPrintRules } from './AutoPrintRules';

export function PrintingPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [code, setCode] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const agents = useQuery({ queryKey: ['print-agents'], queryFn: () => get<AgentDto[]>('/printing/agents'), refetchInterval: 15_000 });
  const printers = useQuery({ queryKey: ['printers'], queryFn: () => get<PrinterDto[]>('/printing/printers'), refetchInterval: 15_000 });
  const jobs = useQuery({ queryKey: ['print-jobs'], queryFn: () => get<{ items: PrintJobDto[] }>('/printing/jobs?perPage=50'), refetchInterval: 5_000 });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['print-agents'] });
    void qc.invalidateQueries({ queryKey: ['printers'] });
    void qc.invalidateQueries({ queryKey: ['print-jobs'] });
  };

  const enrol = useMutation({ mutationFn: () => post<{ code: string }>('/printing/agents/enrolment-code', {}), onSuccess: (r) => setCode(r.code), onError: setError });
  const revoke = useMutation({ mutationFn: (id: string) => del(`/printing/agents/${id}`), onSuccess: invalidate, onError: setError });
  const test = useMutation({ mutationFn: (v: { printerId: string; format: 'pdf' | 'zpl' }) => post('/printing/test', v), onSuccess: invalidate, onError: setError });
  const update = useMutation({ mutationFn: (v: { id: string; patch: Record<string, unknown> }) => patch(`/printing/printers/${v.id}`, v.patch), onSuccess: invalidate, onError: setError });
  const jobAction = useMutation({ mutationFn: (v: { id: string; action: 'retry' | 'cancel' }) => post(`/printing/jobs/${v.id}/${v.action}`), onSuccess: invalidate, onError: setError });

  const statusTone = (s: PrintJobDto['status']) => (s === 'printed' ? 'green' : s === 'failed' ? 'red' : s === 'cancelled' ? 'zinc' : 'amber');

  return (
    <div className="space-y-6">
      <PageTitle
        title="Print"
        actions={
          can('printing.manage') && (
            <div className="flex gap-2">
              <button className="btn" onClick={() => enrol.mutate()}>
                Tilslut med kode
              </button>
              <button className="btn btn-primary" onClick={() => setInstalling(true)}>
                Installér printagent
              </button>
            </div>
          )
        }
      />
      <ErrorBox error={error} />
      {installing && <InstallerPanel onClose={() => setInstalling(false)} />}
      {code && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          Indtast denne kode i agenten inden 10 minutter: <code className="text-lg font-bold tracking-widest">{code}</code>
          <button className="btn ml-3" onClick={() => setCode(null)}>
            Luk
          </button>
        </div>
      )}

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">Agenter</h2>
        {agents.isLoading ? (
          <Spinner />
        ) : (
          <table className="table card p-0">
            <thead>
              <tr>
                <th>Navn</th>
                <th>Status</th>
                <th>Version</th>
                <th>Printere</th>
                <th>Sidst set</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {agents.data?.map((a) => (
                <tr key={a.id}>
                  <td>{a.name}</td>
                  <td>
                    <Badge tone={a.status === 'online' ? 'green' : 'red'}>{a.status}</Badge>
                  </td>
                  <td>{a.version}</td>
                  <td>{a.printerCount}</td>
                  <td>{fmtTime(a.lastSeenAt)}</td>
                  <td>
                    {can('printing.manage') && (
                      <button className="btn btn-danger" onClick={() => confirm(`Fjern agenten ${a.name}?`) && revoke.mutate(a.id)}>
                        Fjern
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {agents.data?.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-zinc-500">
                    Ingen agenter endnu. Tilslut en printagent på en pc i lageret.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">Printere</h2>
        <table className="table card p-0">
          <thead>
            <tr>
              <th>Navn</th>
              <th>Agent</th>
              <th>Type</th>
              <th>Adresse (ZPL)</th>
              <th>Online</th>
              <th>Aktiv</th>
              <th>Test</th>
            </tr>
          </thead>
          <tbody>
            {printers.data?.map((p) => (
              <tr key={p.id}>
                <td>
                  {can('printing.manage') ? (
                    <input className="input" defaultValue={p.displayName} onBlur={(e) => e.target.value !== p.displayName && update.mutate({ id: p.id, patch: { displayName: e.target.value } })} />
                  ) : (
                    p.displayName
                  )}
                  <div className="text-xs text-zinc-400">{p.osName}</div>
                </td>
                <td>{p.agentName}</td>
                <td>
                  <select className="input" value={p.kind} disabled={!can('printing.manage')} onChange={(e) => update.mutate({ id: p.id, patch: { kind: e.target.value } })}>
                    <option value="pdf">PDF (laser)</option>
                    <option value="raw">ZPL (label)</option>
                  </select>
                </td>
                <td>
                  <input className="input" placeholder="tcp://10.0.0.15:9100" defaultValue={p.address ?? ''} disabled={!can('printing.manage')} onBlur={(e) => (e.target.value || null) !== p.address && update.mutate({ id: p.id, patch: { address: e.target.value || null } })} />
                </td>
                <td>
                  <Badge tone={p.online ? 'green' : 'red'}>{p.online ? 'ja' : 'nej'}</Badge>
                </td>
                <td>
                  <input type="checkbox" checked={p.enabled} disabled={!can('printing.manage')} onChange={(e) => update.mutate({ id: p.id, patch: { enabled: e.target.checked } })} />
                </td>
                <td className="space-x-1 whitespace-nowrap">
                  <button className="btn" onClick={() => test.mutate({ printerId: p.id, format: 'pdf' })} disabled={p.kind !== 'pdf' && false}>
                    PDF
                  </button>
                  {p.kind === 'raw' && (
                    <button className="btn" onClick={() => test.mutate({ printerId: p.id, format: 'zpl' })}>
                      ZPL
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <AutoPrintRules onError={setError} />

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">Kø</h2>
        <table className="table card p-0">
          <thead>
            <tr>
              <th>Tid</th>
              <th>Dokument</th>
              <th>Printer</th>
              <th>Status</th>
              <th>Forsøg</th>
              <th>Af</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {jobs.data?.items.map((j) => (
              <tr key={j.id}>
                <td className="whitespace-nowrap">{fmtTime(j.createdAt)}</td>
                <td>
                  {j.documentType} <span className="text-xs text-zinc-400">{j.format}</span>
                </td>
                <td>{j.printerName}</td>
                <td>
                  <Badge tone={statusTone(j.status)}>{j.status}</Badge>
                  {j.lastError && <div className="text-xs text-red-700">{j.lastError}</div>}
                </td>
                <td>{j.attempts}</td>
                <td>{j.requestedBy ?? '–'}</td>
                <td className="space-x-1 whitespace-nowrap">
                  {(j.status === 'failed' || j.status === 'cancelled') && (
                    <button className="btn" onClick={() => jobAction.mutate({ id: j.id, action: 'retry' })}>
                      Prøv igen
                    </button>
                  )}
                  {(j.status === 'queued' || j.status === 'leased') && (
                    <button className="btn" onClick={() => jobAction.mutate({ id: j.id, action: 'cancel' })}>
                      Annullér
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

/** Download of the agent as a zip: it carries the server address and a fresh one-time code. */
function InstallerPanel({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const info = useQuery({ queryKey: ['agent-installer'], queryFn: () => get<{ available: boolean; sizeBytes: number | null }>('/printing/agents/installer-info') });
  const href = `/api/printing/agents/installer?${new URLSearchParams({ name: name.trim() })}`;
  return (
    <div className="card max-w-2xl space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <div className="font-semibold">Installér printagenten på en Windows-pc</div>
        <button className="btn" onClick={onClose}>Luk</button>
      </div>
      <ol className="list-decimal space-y-1 pl-5">
        <li>Giv pc’en et navn, så I kan kende den her (fx "Pakkebord 1").</li>
        <li>Hent zip-filen på pc’en, højreklik og vælg "Udpak alle".</li>
        <li>Dobbeltklik på <code>install.cmd</code> i den udpakkede mappe. Windows beder om administratorrettigheder; uden dem installeres agenten kun for den bruger, der er logget på.</li>
        <li>Agenten forbinder sig selv, finder printerne og starter automatisk med Windows. Printerne dukker op herunder. Den udpakkede mappe kan derefter slettes.</li>
      </ol>
      {info.data && !info.data.available ? (
        <div className="text-danger">Printagenten er ikke pakket på serveren endnu.</div>
      ) : (
        <div className="flex items-center gap-2">
          <input className="input max-w-xs" placeholder="Navn på pc’en" value={name} onChange={(e) => setName(e.target.value)} />
          <a className={`btn btn-primary ${name.trim() ? '' : 'pointer-events-none opacity-50'}`} href={href} download aria-disabled={!name.trim()}>
            Hent zip-fil
          </a>
        </div>
      )}
      <p className="text-xs text-muted">Zip-filen indeholder Node.js’ egen, signerede node.exe, agenten og SumatraPDF til PDF’er. Hver hentning laver en ny engangskode, som gælder i 7 dage. Kør install.cmd fra en ny zip på samme pc for at opdatere agenten; den beholder sin tilslutning. Pc’en skal kunne nå thebb.samsinnovation.dev (Borg & Bigums interne DNS blokerer den: se README).</p>
    </div>
  );
}
